import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  fetchCart,
  addToCart,
  updateCartItemQuantity,
  removeCartItem,
  clearCart,
  checkoutCart,
  type CartResponse,
  type CartItemResponse,
  type CartVariantPopulated,
  type CartProductPopulated,
  type AddToCartPayload,
  type UpdateCartItemPayload,
  type CheckoutPayload,
} from "@/api/cartApi";
import { useAuthStore } from "@/stores/authStore";
import { variantKeys } from "@/hooks/useVariantQueries";

export type { CartResponse, CartItemResponse, CheckoutPayload } from "@/api/cartApi";
import { orderKeys } from "@/hooks/useOrderQueries";

interface AddToCartVariables extends AddToCartPayload {
  _optimistic?: {
    variant: CartVariantPopulated;
    product: CartProductPopulated;
  };
}

export const cartKeys = {
  all: ["cart"] as const,
};

export const useCart = () => {
  const { userInfo } = useAuthStore();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: cartKeys.all,
    queryFn: fetchCart,
    enabled: !!userInfo,
  });

  const expiresAt = query.data?.expiresAt;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!expiresAt) return;

    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      queryClient.invalidateQueries({ queryKey: cartKeys.all });
      return;
    }

    timerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: cartKeys.all });
    }, ms);

    return () => clearTimeout(timerRef.current);
  }, [expiresAt, queryClient]);

  return query;
};

export const useAddToCart = () => {
  const queryClient = useQueryClient();

  return useMutation<CartResponse, unknown, AddToCartVariables>({
    mutationFn: ({ variantId, quantity }: AddToCartVariables) => addToCart({ variantId, quantity }),

    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: cartKeys.all });
      const previousCart = queryClient.getQueryData<CartResponse>(cartKeys.all);

      if (previousCart && variables._optimistic) {
        const { variant, product } = variables._optimistic;
        const existingIndex = previousCart.items.findIndex(
          (i) => i.variant?._id === variables.variantId
        );

        let updatedItems: CartItemResponse[];
        if (existingIndex >= 0) {
          updatedItems = previousCart.items.map((item, idx) =>
            idx === existingIndex ? { ...item, quantity: item.quantity + variables.quantity } : item
          );
        } else {
          const optimisticItem: CartItemResponse = {
            _id: `optimistic-${Date.now()}`,
            variant,
            product,
            category: { _id: "", name: "" },
            branch: { _id: "", name: "" },
            quantity: variables.quantity,
            reservedStock: variables.quantity * product.bunch,
            price: variant.price,
            addedAt: new Date().toISOString(),
          };
          updatedItems = [...previousCart.items, optimisticItem];
        }

        queryClient.setQueryData<CartResponse>(cartKeys.all, {
          ...previousCart,
          items: updatedItems,
          itemCount: previousCart.itemCount + variables.quantity,
          totalPrice: previousCart.totalPrice + variant.price * product.bunch * variables.quantity,
        });
      }

      return { previousCart };
    },

    onError: (error, _variables, context) => {
      const ctx = context as { previousCart?: CartResponse } | undefined;
      if (ctx?.previousCart !== undefined) {
        queryClient.setQueryData(cartKeys.all, ctx.previousCart);
      }
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to add item to cart";
      toast.error("Failed to add item to cart", { description: message });
    },

    onSuccess: (data) => {
      queryClient.setQueryData(cartKeys.all, data);
      toast.success(data.message || "Item added to cart");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
    },
  });
};

export const useUpdateCartItemQuantity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCartItemPayload) => updateCartItemQuantity(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(cartKeys.all, data);
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to update quantity";
      toast.error("Something went wrong", { description: message });
    },
  });
};

export const useRemoveCartItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variantId: string) => removeCartItem(variantId),
    onSuccess: (data) => {
      queryClient.setQueryData(cartKeys.all, data);
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
      toast.success(data.message || "Item removed from cart");
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to remove item";
      toast.error("Something went wrong", { description: message });
    },
  });
};

export const useClearCart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clearCart,
    onSuccess: (data) => {
      queryClient.setQueryData(cartKeys.all, data);
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
      toast.success(data.message || "Cart cleared");
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to clear cart";
      toast.error("Something went wrong", { description: message });
    },
  });
};

export const useCheckoutCart = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CheckoutPayload) => checkoutCart(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(cartKeys.all, {
        items: [],
        itemCount: 0,
        totalPrice: 0,
        expiresAt: null,
      });
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success(data.message || "Order placed successfully!");
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to place order";
      toast.error("Something went wrong", { description: message });
    },
  });
};
